// АНИМ.2: модалка перехода между лигами.
//
// 5 сценариев (event):
//   - promotion         — повышение (▲ ПОВЫШЕНИЕ, 3.5с)
//   - demotion          — понижение (▼ ПОНИЖЕНИЕ, 3.0с)
//   - firewall_consumed — файервол поглотил удар (2.5с)
//   - firewall_gained   — получен +1 файервол (2.0с)
//   - high_league_entry — вход в Высшую (длинная катсцена 5.5с)
//
// Показывается ПОСЛЕ EndOfMatchModal — игрок жмёт «Дальше», модалка
// эндматча закрывается, эта открывается.
//
// При useAnimationsEnabled() === false — статичная композиция: title +
// награды (если есть) + CTA. Без анимаций. Игрок видит ту же информацию.

import React from 'react';
import { Stage, T, Easing, useTime } from '../../components/animation/animations';
import { useAnimationsEnabled } from '../../hooks/useAnimationsEnabled';
import { useTr } from '../../i18n/useTr';
import {
  DataTraces, LeagueBackdrop, LadderHologram, LeagueNameSwap, HeroTitle,
  RewardCascade, CTAForward, ShieldIcon, HexShield, TopCaption, hexA,
  type RewardChipData
} from './parts';
import type { LeagueUiDef } from './leaguesData';
import type { LeagueEvent } from './useLeagueEvent';
import { play } from '../../audio/sfx';

const COLORS = {
  cy: '#3DF0FF', mg: '#FF3D8F', amber: '#FFB13D',
  lime: '#9CFF3D', red: '#FF3D3D',
  md: '#A0A4B4', lo: '#60657A', hi: '#F4F6FB'
};

export interface LeagueTransitionModalProps {
  event: LeagueEvent;
  fromLeague: LeagueUiDef;
  toLeague: LeagueUiDef;
  remainingFirewalls: number;
  rewardDescription: string | null;
  onClose: () => void;
}

// Длительность сцены по сценарию (Stage.duration).
const DURATIONS: Record<LeagueEvent, number> = {
  promotion: 4.0,
  demotion: 3.5,
  firewall_consumed: 3.0,
  firewall_gained: 2.5,
  high_league_entry: 6.0
};

export function LeagueTransitionModal(props: LeagueTransitionModalProps) {
  const enabled = useAnimationsEnabled();
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';

  // Этап 71: звук сценария (один раз при появлении модалки).
  React.useEffect(() => {
    const sfx: Record<LeagueEvent, string> = {
      promotion: 'leagueUp',
      demotion: 'leagueDown',
      firewall_consumed: 'shield',  // файервол поглотил удар — «щит»
      firewall_gained: 'buff',
      high_league_entry: 'leagueUp'
    };
    play(sfx[props.event]);
  }, []);

  // Парсим rewardDescription в массив чипов (если есть).
  const rewards = React.useMemo<RewardChipData[]>(() => {
    if (!props.rewardDescription) return [];
    return parseRewardChips(props.rewardDescription);
  }, [props.rewardDescription]);

  if (!enabled) {
    return <NoAnimRenderer {...props} rewards={rewards} isEn={isEn} />;
  }

  return (
    <Stage
      width={720}
      height={1280}
      duration={DURATIONS[props.event]}
      background="#050509"
    >
      <SceneRouter {...props} rewards={rewards} isEn={isEn} />
    </Stage>
  );
}

function SceneRouter(
  props: LeagueTransitionModalProps & { rewards: RewardChipData[]; isEn: boolean }
) {
  switch (props.event) {
    case 'promotion':         return <ScenePromotion {...props} />;
    case 'demotion':          return <SceneDemotion {...props} />;
    case 'firewall_consumed': return <SceneFirewallConsumed {...props} />;
    case 'firewall_gained':   return <SceneFirewallGained {...props} />;
    case 'high_league_entry': return <SceneHighLeague {...props} />;
  }
}

// ─── ScenePromotion ──────────────────────────────────────────────
function ScenePromotion(
  props: LeagueTransitionModalProps & { rewards: RewardChipData[]; isEn: boolean }
) {
  const t = useTime();
  const subOp = T(t, 2.0, 2.4, 0, 1, Easing.easeOutCubic);
  const tr = useTr();
  const subText = props.isEn
    ? `${props.fromLeague.en} → ${props.toLeague.en}`
    : `${props.fromLeague.ru} → ${props.toLeague.ru}`;

  return (
    <>
      <LeagueBackdrop fromL={props.fromLeague} toL={props.toLeague} swapAt={1.0} />
      <DataTraces color={props.toLeague.color} count={14} opacity={0.4} speed={120} />
      <TopCaption at={0.1} text={tr('⟢ ИЗМЕНЕНИЕ ЛИГИ ⟣', '⟢ LEAGUE CHANGE ⟣')} />
      {/* БУГ.9: новая раскладка — «ПОВЫШЕНИЕ» ВЫШЕ лестницы. Раньше
          лестница (центр 480, height 420 → 280-700) накладывалась
          на текст. Теперь:
            y=140  — имя новой лиги (top)
            y=320  — «▲ ПОВЫШЕНИЕ» (заметный, в верхней трети)
            y=420  — sub-line «X → Y»
            y=720  — лестница с фигуркой (центр 720, height 420 → 510-930)
            y=1020 — награды (под лестницей)
            CTA — auto bottom */}
      <LeagueNameSwap
        fromL={props.fromLeague}
        toL={props.toLeague}
        swapAt={0.8}
        y={140}
        isEn={props.isEn}
      />
      <HeroTitle
        at={1.9}
        text={tr('▲ ПОВЫШЕНИЕ', '▲ PROMOTION')}
        color={COLORS.lime}
        size={56}
        y={320}
      />
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 410,
        textAlign: 'center', opacity: subOp,
        color: COLORS.md, fontFamily: "'Inter', sans-serif",
        fontSize: 17, letterSpacing: '0.03em'
      }}>{subText}</div>
      <LadderHologram
        fromStep={1}
        toStep={2}
        color={props.toLeague.color}
        startAt={0.35}
        moveAt={1.0}
        y={720}
      />
      {props.rewards.length > 0 && (
        <RewardCascade rewards={props.rewards} startAt={2.4} y={1020} />
      )}
      <CTAForward
        at={3.2}
        label={tr('ВПЕРЁД', 'FORWARD')}
        color={COLORS.cy}
        onClick={props.onClose}
      />
    </>
  );
}

// ─── SceneDemotion ───────────────────────────────────────────────
function SceneDemotion(
  props: LeagueTransitionModalProps & { rewards: RewardChipData[]; isEn: boolean }
) {
  const t = useTime();
  const tr = useTr();
  // Camera shake: amplitude 0..8px между 0.4 и 0.9с, потом затухание.
  const shake = (T(t, 0.4, 0.9, 0, 8, Easing.easeOutCubic) *
                 Math.sin(t * 50) *
                 Math.max(0, 1 - (t - 0.9) * 1.5));
  const cracksOp = T(t, 0.1, 0.6, 0, 0.9, Easing.easeOutCubic);
  const copyOp = T(t, 2.4, 2.9, 0, 1, Easing.easeOutCubic);

  return (
    <div style={{ position: 'absolute', inset: 0, transform: `translate(${shake}px, ${shake * 0.5}px)` }}>
      <LeagueBackdrop fromL={props.fromLeague} toL={props.toLeague} swapAt={1.4} />
      {/* Красные трещины */}
      <svg style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        opacity: cracksOp, pointerEvents: 'none'
      }} viewBox="0 0 720 1280">
        {[
          'M 100 200 L 240 380 L 180 540 L 320 720',
          'M 580 150 L 460 340 L 540 520 L 420 700',
          'M 0 800 L 180 920 L 80 1060 L 240 1180',
          'M 720 850 L 520 980 L 620 1100 L 460 1240'
        ].map((d, i) => (
          <path key={i} d={d} stroke={COLORS.red} strokeWidth="2" fill="none" opacity={0.5 + (i % 2) * 0.3} />
        ))}
      </svg>
      <TopCaption at={0.1} text={tr('⟢ ИЗМЕНЕНИЕ ЛИГИ ⟣', '⟢ LEAGUE CHANGE ⟣')} />
      {/* БУГ.9: ПОНИЖЕНИЕ — аналогичная раскладка с ПОВЫШЕНИЕМ. */}
      <LeagueNameSwap
        fromL={props.fromLeague}
        toL={props.toLeague}
        swapAt={1.2}
        y={140}
        isEn={props.isEn}
      />
      <HeroTitle
        at={2.0}
        text={tr('▼ ПОНИЖЕНИЕ', '▼ DEMOTION')}
        color={COLORS.red}
        size={54}
        y={320}
      />
      <LadderHologram
        fromStep={2}
        toStep={1}
        color={COLORS.red}
        startAt={0.2}
        moveAt={0.8}
        y={720}
      />
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 1020,
        textAlign: 'center', opacity: copyOp,
        color: COLORS.md, fontFamily: "'Inter', sans-serif",
        fontSize: 15, lineHeight: 1.5, padding: '0 60px'
      }}>
        {tr(
          'Тебя откатили обратно. Файервол не помог.',
          'You got bumped back. The firewall did not hold.'
        )}
      </div>
      <CTAForward
        at={2.8}
        label={tr('ВПЕРЁД', 'FORWARD')}
        color={COLORS.cy}
        onClick={props.onClose}
      />
    </div>
  );
}

// ─── SceneFirewallConsumed ───────────────────────────────────────
function SceneFirewallConsumed(
  props: LeagueTransitionModalProps & { rewards: RewardChipData[]; isEn: boolean }
) {
  const t = useTime();
  const tr = useTr();
  const melting = T(t, 0.5, 1.6, 0, 1, Easing.easeInCubic);
  const glow = T(t, 0.1, 0.5, 0, 1, Easing.easeOutCubic) -
               T(t, 1.2, 2.0, 0, 1, Easing.easeInCubic);
  const countOp = T(t, 1.6, 2.0, 0, 1, Easing.easeOutCubic);
  const hexOp = T(t, 1.7, 2.1, 0, 1, Easing.easeOutCubic);

  return (
    <>
      {/* Янтарный фон */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 50% 40%, ${hexA(COLORS.amber, 0.22)} 0%, ${hexA(COLORS.amber, 0.05)} 40%, #07070B 80%)`
      }} />
      <DataTraces color={COLORS.amber} count={10} opacity={0.3} speed={80} />
      <ShieldIcon size={140} color={COLORS.amber} melting={melting} glow={Math.max(0, glow)} y={460} />
      <HeroTitle
        at={0.9}
        text={tr('🛡 ФАЙЕРВОЛ ПОГЛОТИЛ УДАР', '🛡 FIREWALL ABSORBED THE HIT')}
        color={COLORS.amber}
        size={32}
        y={720}
      />
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 820,
        textAlign: 'center', opacity: countOp,
        color: COLORS.md, fontFamily: "'JetBrains Mono', monospace",
        fontSize: 14, letterSpacing: '0.2em'
      }}>
        {tr(
          `ОСТАЛОСЬ ФАЙЕРВОЛОВ: ${props.remainingFirewalls}`,
          `FIREWALLS LEFT: ${props.remainingFirewalls}`
        )}
      </div>
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 870,
        display: 'flex', justifyContent: 'center', gap: 16,
        opacity: hexOp
      }}>
        {[0, 1, 2].map((i) => (
          <HexShield key={i} filled={i < props.remainingFirewalls} color={COLORS.amber} />
        ))}
      </div>
      <CTAForward
        at={2.2}
        label={tr('ВПЕРЁД', 'FORWARD')}
        color={COLORS.cy}
        onClick={props.onClose}
      />
    </>
  );
}

// ─── SceneFirewallGained ─────────────────────────────────────────
function SceneFirewallGained(
  props: LeagueTransitionModalProps & { rewards: RewardChipData[]; isEn: boolean }
) {
  const t = useTime();
  const tr = useTr();
  const labelOp = T(t, 1.4, 1.8, 0, 1, Easing.easeOutCubic);
  const shieldGlow = T(t, 0.8, 1.2, 0, 0.8, Easing.easeOutCubic);

  // 12 частиц-точек, сходящихся к центру.
  const particles = React.useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({
      angle: (i / 12) * Math.PI * 2,
      dist: 220 + (i % 3) * 30,
      delay: (i % 4) * 0.04
    })),
    []
  );

  return (
    <>
      {/* Янтарный фон + tunnel-маска */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at 50% 38%, ${hexA(COLORS.amber, 0.22)} 0%, ${hexA(COLORS.amber, 0.04)} 50%, #07070B 80%)`
      }} />
      {/* Vignette-маска, убирается при сборке (t = 0.8..0.9) */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(circle at 50% 38%, transparent 80px, #07070B 130px)`,
        opacity: T(t, 0.8, 0.9, 1, 0, Easing.linear)
      }} />

      {/* Частицы */}
      {particles.map((p, i) => {
        const local = t - 0.2 - p.delay;
        const prog = Math.min(1, Math.max(0, local / 0.8));
        const ease = Easing.easeInCubic(prog);
        const x = Math.cos(p.angle) * p.dist * (1 - ease);
        const y = Math.sin(p.angle) * p.dist * (1 - ease);
        return (
          <div key={i} style={{
            position: 'absolute',
            left: 360 + x - 3, top: 480 + y - 3,
            width: 6, height: 6,
            borderRadius: '50%',
            background: COLORS.amber,
            opacity: prog * (1 - prog * 0.3),
            boxShadow: `0 0 8px ${COLORS.amber}`
          }} />
        );
      })}

      <ShieldIcon size={120} color={COLORS.amber} melting={0} glow={shieldGlow} y={480} />

      <HeroTitle
        at={1.2}
        text={tr('+1 ФАЙЕРВОЛ', '+1 FIREWALL')}
        color={COLORS.amber}
        size={48}
        y={720}
      />
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 800,
        textAlign: 'center', opacity: labelOp,
        color: COLORS.md, fontFamily: "'Inter', sans-serif",
        fontSize: 16
      }}>
        {tr('Защита от слива.', 'Defense against being bumped down.')}
      </div>
      <CTAForward
        at={1.7}
        label={tr('ВПЕРЁД', 'FORWARD')}
        color={COLORS.cy}
        onClick={props.onClose}
      />
    </>
  );
}

// ─── SceneHighLeague ─────────────────────────────────────────────
function SceneHighLeague(
  props: LeagueTransitionModalProps & { rewards: RewardChipData[]; isEn: boolean }
) {
  const t = useTime();
  const tr = useTr();
  const beamOp = T(t, 0.9, 1.4, 0, 0.8, Easing.easeOutCubic) -
                 T(t, 2.0, 2.8, 0, 0.5, Easing.easeInCubic);
  const heroOp = T(t, 1.4, 2.0, 0, 1, Easing.easeOutCubic);
  const loreOp = 0.85 * T(t, 3.0, 3.5, 0, 1, Easing.easeOutCubic);

  return (
    <>
      {/* Янтарный платиновый градиент */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(180deg, #1A1208 0%, #2A1F10 50%, #050509 100%)'
      }} />
      <DataTraces color={COLORS.amber} count={20} opacity={0.6} speed={140} />

      {/* Beam-down (трапеция сверху) */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 0, height: '60%',
        background: `linear-gradient(180deg, ${hexA(COLORS.amber, 0.6)} 0%, transparent 100%)`,
        clipPath: 'polygon(40% 0, 60% 0, 100% 100%, 0 100%)',
        opacity: Math.max(0, beamOp)
      }} />

      {/* Hero silhouette */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 280,
        display: 'flex', justifyContent: 'center', opacity: heroOp
      }}>
        <svg width="200" height="320" viewBox="0 0 100 160">
          <circle cx="50" cy="22" r="14" fill={COLORS.amber} opacity="0.85" />
          <path d="M 18 158 L 18 70 Q 18 48 50 48 Q 82 48 82 70 L 82 158 Z"
                fill={COLORS.amber} opacity="0.7" />
          <path d="M 25 158 L 25 70 Q 25 50 50 50 Q 75 50 75 70 L 75 158 Z"
                fill="none" stroke="#FFC83D" strokeWidth="0.5" opacity="0.9" />
        </svg>
      </div>

      <TopCaption
        at={0.2}
        text={tr('⟢ СЕЗОННОЕ СОБЫТИЕ ⟣', '⟢ SEASONAL EVENT ⟣')}
        color={COLORS.amber}
        glow
      />
      <HeroTitle
        at={2.2}
        text={tr('ТЫ В ВЫСШЕЙ ЛИГЕ', 'YOU ARE IN BIG LEAGUE')}
        color={COLORS.amber}
        size={44}
        y={830}
      />
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 910,
        textAlign: 'center', opacity: loreOp,
        color: COLORS.amber, fontFamily: "'Inter', sans-serif",
        fontStyle: 'italic', fontSize: 17, letterSpacing: '0.02em'
      }}>
        {tr('Теперь смотрят все.', 'Now everyone is watching.')}
      </div>
      {props.rewards.length > 0 && (
        <RewardCascade rewards={props.rewards} startAt={4.0} y={980} />
      )}
      <CTAForward
        at={5.0}
        label={tr('ВПЕРЁД', 'FORWARD')}
        color={COLORS.amber}
        onClick={props.onClose}
      />
    </>
  );
}

// ─── NoAnimRenderer ──────────────────────────────────────────────
// Статичная композиция для useAnimationsEnabled() === false.
function NoAnimRenderer(
  props: LeagueTransitionModalProps & { rewards: RewardChipData[]; isEn: boolean }
) {
  const tr = useTr();
  const { title, color, sub } = staticTitleFor(props, tr);
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#050509',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 24, padding: 40, zIndex: 100000
    }}>
      <div style={{
        color, fontFamily: "'Chakra Petch', sans-serif",
        fontSize: 38, fontWeight: 700, letterSpacing: '0.08em',
        textAlign: 'center'
      }}>{title}</div>
      {sub && (
        <div style={{
          color: COLORS.md, fontFamily: "'Inter', sans-serif", fontSize: 16,
          textAlign: 'center'
        }}>{sub}</div>
      )}
      {props.rewards.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
          {props.rewards.map((r, i) => (
            <div key={i} style={{
              padding: '8px 14px',
              border: `1px solid ${r.color}`,
              color: r.color,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 13
            }}>
              {r.icon} {r.label}
            </div>
          ))}
        </div>
      )}
      <button
        onClick={props.onClose}
        style={{
          marginTop: 16, padding: '14px 60px',
          background: hexA(COLORS.cy, 0.12),
          border: `1.5px solid ${COLORS.cy}`,
          color: COLORS.cy,
          fontFamily: "'Chakra Petch', sans-serif", fontSize: 18,
          fontWeight: 700, letterSpacing: '0.15em',
          cursor: 'pointer'
        }}
      >{tr('ВПЕРЁД', 'FORWARD')}</button>
    </div>
  );
}

function staticTitleFor(
  props: LeagueTransitionModalProps & { isEn: boolean },
  tr: (ru: string, en: string) => string
): { title: string; color: string; sub: string | null } {
  const from = props.isEn ? props.fromLeague.en : props.fromLeague.ru;
  const to = props.isEn ? props.toLeague.en : props.toLeague.ru;
  switch (props.event) {
    case 'promotion':
      return {
        title: tr('▲ ПОВЫШЕНИЕ', '▲ PROMOTION'),
        color: COLORS.lime,
        sub: `${from} → ${to}`
      };
    case 'demotion':
      return {
        title: tr('▼ ПОНИЖЕНИЕ', '▼ DEMOTION'),
        color: COLORS.red,
        sub: `${from} → ${to}`
      };
    case 'firewall_consumed':
      return {
        title: tr('ФАЙЕРВОЛ ПОГЛОТИЛ УДАР', 'FIREWALL ABSORBED THE HIT'),
        color: COLORS.amber,
        sub: tr(
          `Осталось файерволов: ${props.remainingFirewalls}`,
          `Firewalls left: ${props.remainingFirewalls}`
        )
      };
    case 'firewall_gained':
      return {
        title: tr('+1 ФАЙЕРВОЛ', '+1 FIREWALL'),
        color: COLORS.amber,
        sub: tr('Защита от слива.', 'Defense against being bumped down.')
      };
    case 'high_league_entry':
      return {
        title: tr('ТЫ В ВЫСШЕЙ ЛИГЕ', 'YOU ARE IN BIG LEAGUE'),
        color: COLORS.amber,
        sub: tr('Теперь смотрят все.', 'Now everyone is watching.')
      };
  }
}

// Парсер description'а из LeaguePromotionReward в массив чипов.
// Пример description: "+80 Скр, +1 Прод. ШК"
function parseRewardChips(desc: string): RewardChipData[] {
  return desc.split(/[,;]/).map((s) => s.trim()).filter(Boolean).map((part) => {
    const lower = part.toLowerCase();
    if (lower.includes('скр')) return { label: part, color: COLORS.cy, icon: '§' };
    if (lower.includes('шк')) return { label: part, color: COLORS.amber, icon: '▣' };
    if (lower.includes('cover') || lower.includes('обложк')) {
      return { label: part, color: COLORS.amber, icon: '★' };
    }
    return { label: part, color: COLORS.md, icon: '•' };
  });
}
